import {
  CreativeTemplate,
  CreativeTemplateFeature,
  CreativeTemplateSource,
  CreativeTemplateSourceType,
  RemoteCreativeTemplate,
} from '../creativeTemplates/creativeTemplates.types';
import {CLIssues} from '../issues/issues.types';
import {CLEnum} from 'common/src/modules/enums/enums.types';
import {
  DPDynamicSourceType,
  DynamicParametersType,
} from '../dynamicParameters/dynamicParameters.enums';
import {DateTime} from 'luxon';
import {ProductType, ProductTypeName} from '../products/products.enums';
import {Widget, DPWidgetValue} from '../dynamicParameters/widgets/widgets.types';
import {NameValue} from '../../types/general.types';
import {CampaignUsage} from '../campaigns/campaigns.types';
import {TaxonomyType} from '../taxonomy/taxonomy.types';

enum CLCreativeType {
  html = 'HTML',
  video = 'Video',
  image = 'Image',
  audio = 'Audio',
  interactiveVideo = 'InteractiveVideo',
  inBannerVideo = 'InBannerVideo',
}

export enum CreativeType {
  Html,
  Video,
  Image,
  Audio,
  InteractiveVideo,
  InBannerVideo,
}

export enum AiCreativeType {
  Html = 'HTML',
  Video = 'Video',
  Image = 'Image',
  Audio = 'Audio',
  InteractiveVideo = 'Interactive Video',
  InBannerVideo = 'In-Banner Video',
}

enum StaticCreativeType {
  Image = 10,
  InBannerVideo = 20,
  Html = 30,
  Video = 40,
  Audio = 50,
}

enum EccLevel {
  Low = 'L',
  Medium = 'M',
  Quality = 'Q',
  High = 'H',
}

interface RemoteCampaignCreative {
  baseStyleId: number;
  baseStyleItemCount: number;
  campaignId: number;
  campaignType: number;
  clientstyleId?: number;
  creativeId: number;
  id: number;
  landingPage: string;
  landingPageSource: number;
  optimizationVariantId: number;
  productType: number;
  utmParams: string;
  servingpercent?: number;
  clientStyle: RemoteCreative;
}

interface CampaignCreative extends Omit<RemoteCampaignCreative, 'clientStyle'> {
  clientStyle: Creative;
}

interface CreativeFormat {
  Id: number;
  RendererVersion: number;
  width: number;
  height: number;
  minItemCount: number;
  maxItemCount: number;
  verificationResult?: CreativeVerificationResult;
  videoProp?: CreativeVideoData;
  adFormat: number;
  issues?: CLIssues;
}

interface CreativeVideoData {
  duration: number;
  fps: number;
  videoBitRate: number;
  audioSampleRate: number;
  audioSampleSize: number;
  audioBitRate: number;
}

interface CreativeVerificationResult {
  formatId: number;
  initialLoadSize: number;
  subLoadSize: number;
  totalSize: number;
  loadingTime: number;
  unauthorizedDomains: string[];
  posterFileUrl: string;
}

interface RemoteDynamicParameterValue {
  v: string;
  h: number;
  w: number;
  isDynamic: boolean;
  dpSourceType: DPDynamicSourceType[];
}

interface DynamicParameterValue {
  value: string;
  height: number;
  width: number;
  isDynamic: boolean;
  dynamicSourceType: DPDynamicSourceType;
}

interface RemoteBaseDP {
  name: string;
  description: string;
  category: number;
  dynamicCategory: string;
  selectorOptions: NameValue[];
  isTaxonomized: boolean;
  taxonomyType?: TaxonomyType;
}

interface RemoteDefaultDP extends RemoteBaseDP {
  type: Exclude<DynamicParametersType, DynamicParametersType.Widget>;
  values: Record<string, RemoteDynamicParameterValue>;
}

interface RemoteWidgetDP extends RemoteBaseDP {
  type: DynamicParametersType.Widget;
  values: Record<
    string,
    Omit<RemoteDynamicParameterValue, 'v'> & {
      v: {id: number; args: Record<string, DPWidgetValue>; uiName: string; scriptUrl?: string};
    }
  >;
}

interface QRCodeDPValue {
  text: string;
  eccLevel: EccLevel;

  // both colors are argb hex strings (with 8 characters)
  foregroundColor: string;
  backgroundColor: string;
}

interface RemoteQRCodeDP extends RemoteBaseDP {
  type: DynamicParametersType.QR;
  values: Record<
    string,
    Omit<RemoteDynamicParameterValue, 'v'> & {
      v: QRCodeDPValue;
    }
  >;
}

interface SubtitlesDPValue {
  subtitles: DPSubtitle[];
}

interface DPSubtitle {
  file: {value: string; dpSourceType: DPDynamicSourceType};
  language: {value: string; dpSourceType: DPDynamicSourceType};
}

interface RemoteSubtitlesDP extends RemoteBaseDP {
  type: DynamicParametersType.Subtitles;
  values: Record<
    string,
    Omit<RemoteDynamicParameterValue, 'v'> & {
      v: SubtitlesDPValue;
    }
  >;
}

type RemoteDynamicParameter = RemoteDefaultDP | RemoteWidgetDP | RemoteQRCodeDP | RemoteSubtitlesDP;

interface DynamicParameter extends Omit<RemoteDynamicParameter, 'values'> {
  values: Record<string, DynamicParameterValue>;
  paramKey: string;
}

// isSaved: is this a new dynamicParam that hasn't been saved yet
// isModified: has this dynamicParam been modified since it was last saved
interface DynamicParamWithSavedState extends DynamicParameter {
  isSaved: boolean;
  isModified: boolean;
  shouldScrollIntoView?: boolean;
}

interface CreativeTags {
  tag1Value?: string;
  tag2Value?: string;
  tag3Value?: string;
}

interface TemplateEssentials
  extends Pick<
    CreativeTemplate,
    | 'id'
    | 'name'
    | 'type'
    | 'features'
    | 'productType'
    | 'instructions'
    | 'minItemsCount'
    | 'maxItemsCount'
    | 'clientShareInfo'
  > {
  source: CreativeTemplateSource;
  formats: {
    minItemCount: number;
    itemCount: number;
  }[];

  defaultLocationFeedId: number;
  defaultProductFeedId: number;
  defaultContentFeedId: number;
}

interface RemoteTemplateEssentials extends Omit<TemplateEssentials, 'source'> {
  source: CreativeTemplateSourceType | CreativeTemplateSource;
}

interface RemoteCreative extends Omit<CreativeNodeData, 'template' | 'formats' | 'type'> {
  clientId?: number;
  status: number;
  baseStyle: RemoteCreativeTemplate;
  type: number;
  country: string;
  language?: string;
  previewHtmlIframeURL: string;
  previewThumbnailURL: string;
  previewThumbnailUrl: string;

  dynamicParameters?: Record<string, RemoteDynamicParameter>;
  relatedWidgets?: Record<number, Widget>;
  hasDynamicParameters?: boolean;
  createTime: string;
  updateTime: string;
  updatedByUserName: string;
  createdByUserName: string;
  template?: RemoteTemplateEssentials;
  isDefaultEnabled?: boolean;
  usedInCampaigns: CampaignUsage[];

  /** in static videos */
  formats: CreativeFormat[];
}

type Creative = Omit<
  RemoteCreative,
  | 'formats'
  | 'type'
  | 'dynamicParameters'
  | 'updateTime'
  | 'productType'
  | 'createTime'
  | 'template'
> & {
  template: TemplateEssentials;
  type: CLEnum<CLCreativeType>;
  productType: CLEnum<ProductTypeName, ProductType>;
  formats: CreativeFormat[];
  dynamicParameters?: Record<string, DynamicParameter>;
  updateTime?: DateTime;
  createTime?: DateTime;
};

interface StaticCreativeData {
  [key: string]: {
    features: string[];
    fileName: string;
    type: number;
    videoProp: {
      audioBitRate: number;
      audioSampleRate: number;
      audioSampleSize: number;
      duration: number;
      fps: number;
      videoBitRate: number;
    };
  };
}

interface CreativeConditions extends Record<string, unknown> {
  schedule: {
    dateRanges?: {
      start: string;
      end: string;
      $$hashKey: string;
    }[];
    daysOfWeek2?: null | number;
    timezoneSource?: number;
    timeZoneStr?: string;
    manualTimezoneOffsetMinutes?: number;
  };
  showConditions: {
    $type?: string;
    key?: string;
    condOperator?: number;
    values?: string[];
  };
  utmParams: string;
  landingPageSource: number;
  landingPage: string;
  servingPercent: number;
}

export enum CreativePreviewDrawerSearchParamKey {
  FeedId = 'feedId',
  ProductSet = 'pSetId',
  TopItemList = 'topItemsId',
  CategoryTopItem = 'catTopItemsId',
  CategoryTopItemName = 'catName',
}

export const MINIMUM_NATIVE_LOGO_SIZE = 50;

export interface BaseCreativeFormatData {
  width: number;
  height: number;
  minItemCount: number;
  maxItemCount: number;
  videoProp?: CreativeVideoData;
  issues?: CLIssues;
  verificationResult?: CreativeVerificationResult;
  rendererVersion: number;
  isAvailable?: boolean;
}

export interface CreativeNodeData extends CreativeTags {
  id: number;
  name: string;
  poster: string;
  productType: ProductType;
  type: CreativeType;
  features: CreativeTemplateFeature[];
  staticCreativeData: StaticCreativeData;
  template: TemplateEssentials;
  formats: BaseCreativeFormatData[];
  issues?: CLIssues;
}

interface StaticCreativeFormItem {
  id: number;
  name: string;
  formats: {file: File; url: string; width?: number; height?: number}[];
  errors?: string[];
  warnings?: string[];
}

export {
  CLCreativeType,
  RemoteCreative,
  Creative,
  CampaignCreative,
  RemoteCampaignCreative,
  StaticCreativeData,
  CreativeFormat,
  CreativeVideoData,
  CreativeVerificationResult,
  CreativeConditions,
  RemoteDynamicParameter,
  DynamicParameter,
  RemoteDynamicParameterValue,
  DynamicParameterValue,
  DynamicParamWithSavedState,
  CreativeTags,
  StaticCreativeType,
  RemoteTemplateEssentials,
  TemplateEssentials,
  RemoteWidgetDP,
  RemoteQRCodeDP,
  RemoteSubtitlesDP,
  EccLevel,
  QRCodeDPValue,
  SubtitlesDPValue,
  DPSubtitle,
  StaticCreativeFormItem,
};
